/**
 * Локали интерфейса. Тексты лежат в `locales/<code>.json` (пространства имён → ключи).
 * Добавить язык: положить `locales/xx.json` с той же структурой, что `en.json`,
 * и вписать строку в `locales` ниже — больше ничего менять не нужно.
 * Недостающие ключи падают в `en`; форма множественного числа выбирается через Intl.PluralRules.
 */
import en from './locales/en.json';
import ru from './locales/ru.json';

export const locales = [
  { code: 'en', label: 'English', dictionary: en },
  { code: 'ru', label: 'Русский', dictionary: ru },
] as const;

export type LocaleCode = (typeof locales)[number]['code'];
export const DEFAULT_LOCALE: LocaleCode = 'en';

/**
 * Локали, которые реально можно выбрать в интерфейсе (переключатель, сохранённое
 * значение в localStorage). Продуктовое решение, не техническое: ru переведён и
 * работает (см. `dictionary.test.ts`), просто пока не предлагается пользователю.
 * Чтобы вернуть выбор языка — верни локаль сюда, не трогая `locales` выше.
 */
const SELECTABLE_CODES: readonly LocaleCode[] = ['en'];
export const selectableLocales = locales.filter((l) => SELECTABLE_CODES.includes(l.code));

/** Формы множественного числа по категориям Intl.PluralRules; `other` обязательна. */
export interface PluralForms {
  zero?: string;
  one?: string;
  two?: string;
  few?: string;
  many?: string;
  other: string;
}

type Dictionary = typeof en;

type LeafPaths<T> = {
  [K in keyof T & string]: T[K] extends string
    ? K
    : T[K] extends PluralForms
      ? never
      : T[K] extends object
        ? `${K}.${LeafPaths<T[K]>}`
        : never;
}[keyof T & string];

type PluralPaths<T> = {
  [K in keyof T & string]: T[K] extends string
    ? never
    : T[K] extends PluralForms
      ? K
      : T[K] extends object
        ? `${K}.${PluralPaths<T[K]>}`
        : never;
}[keyof T & string];

/** Ключ обычной строки: `nav.catalog`, `tour.steps.search.title`. */
export type UiKey = LeafPaths<Dictionary>;
/** Ключ строки с формами числа: `catalog.references`. */
export type PluralKey = PluralPaths<Dictionary>;
export type Vars = Record<string, string | number>;

const dictionaries: Record<LocaleCode, unknown> = Object.fromEntries(
  locales.map((l) => [l.code, l.dictionary]),
) as Record<LocaleCode, unknown>;

/**
 * Валидна ли локаль для выбора пользователем (переключатель, значение из
 * localStorage) — то есть входит в `selectableLocales`, а не в полный `locales`.
 * `translate`/`translatePlural` продолжают понимать все локали из `locales`
 * напрямую по литералу — это не про них.
 */
export function isLocaleCode(value: unknown): value is LocaleCode {
  return selectableLocales.some((l) => l.code === value);
}

function lookup(lang: LocaleCode, key: string): unknown {
  let node: unknown = dictionaries[lang];
  for (const part of key.split('.')) {
    if (node === null || typeof node !== 'object') return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return node;
}

function resolve(lang: LocaleCode, key: string): unknown {
  return lookup(lang, key) ?? lookup(DEFAULT_LOCALE, key);
}

/** Подстановка `{name}` из vars; неизвестные плейсхолдеры остаются как есть. */
export function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

const pluralRules = new Map<LocaleCode, Intl.PluralRules>();
function rulesFor(lang: LocaleCode): Intl.PluralRules {
  let rules = pluralRules.get(lang);
  if (!rules) {
    rules = new Intl.PluralRules(lang);
    pluralRules.set(lang, rules);
  }
  return rules;
}

export function translate(lang: LocaleCode, key: UiKey, vars?: Vars): string {
  const value = resolve(lang, key);
  if (typeof value === 'string') return interpolate(value, vars);
  if (value && typeof value === 'object' && 'other' in value) {
    return interpolate(String((value as PluralForms).other), vars);
  }
  return key;
}

export function translatePlural(lang: LocaleCode, key: PluralKey, n: number, vars?: Vars): string {
  const forms = resolve(lang, key) as PluralForms | undefined;
  if (!forms || typeof forms !== 'object') return `${n} ${key}`;
  const category = rulesFor(lang).select(n) as keyof PluralForms;
  const template = forms[category] ?? forms.other;
  return interpolate(template, { n, ...vars });
}

/** Функции перевода, привязанные к языку. */
export interface Translator {
  lang: LocaleCode;
  t: (key: UiKey, vars?: Vars) => string;
  tp: (key: PluralKey, n: number, vars?: Vars) => string;
}

export function translatorFor(lang: LocaleCode): Translator {
  return {
    lang,
    t: (key, vars) => translate(lang, key, vars),
    tp: (key, n, vars) => translatePlural(lang, key, n, vars),
  };
}
